/* eslint-disable @typescript-eslint/no-unsafe-member-access -- lemme cook */
/* eslint-disable @typescript-eslint/no-unsafe-call -- lemme cook */

/* eslint-disable @typescript-eslint/no-unsafe-assignment -- lemme cook */
/* eslint-disable @typescript-eslint/no-explicit-any -- lemme cook*/
/* eslint-disable @typescript-eslint/no-redundant-type-constituents -- it's oke*/

import { useState } from "react";
import type { ValidationError, Schema } from "yup";

// eslint-disable-next-line sonarjs/redundant-type-aliases
type Value = any;
type Values = Record<string, Value>;

const applyMaskTyped = (mask: string, value: string) => {
  const count = mask.split("_").length - 1;

  if (value && count !== 0 && value.length >= count) {
    const maskedSymbols = Array.from(mask.replace(/_/g, ""));
    const unmaskedValue = Array.from(value).filter(
      (val: string) => !maskedSymbols.includes(val)
    );

    return Array.from(mask)
      .map((val: string) => (val === "_" ? unmaskedValue.shift() : val))
      .join("");
  }
  return value;
};

type RegisterTyped<T> = (
  key: keyof T,
  map?: (v: Value) => Value,
  mask?: string
) => {
  name: string;
  value: any;
  onChange: (e: any) => void;
  error: any;
};

type SubmitTyped<T> = ({
  onSuccess,
  onError
}: {
  onSuccess?: (values: T) => void | Promise<void>;
  onError?: (errors: Partial<Record<keyof T, string>>) => void | Promise<void>;
}) => (e: any) => Promise<unknown> | unknown | undefined;

export const useFormTypedWithPartial = <T extends Values>(
  initialValues: T,
  validationSchema?: Schema
) => {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  type InputEvent = React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
  type InputValue = string | number | boolean;

  const handleChange =
    (
      key: keyof T,
      customMap?: (value: InputValue | unknown, prev: Value) => Value,
      mask?: string
    ) =>
    (e?: InputEvent | InputValue) => {
      let value: InputValue | unknown;

      if (e && typeof e === "object" && "target" in e) {
        // safely access target.value or target.checked for checkboxes
        const target = e.target as HTMLInputElement;
        value = target.type === "checkbox" ? target.checked : target.value;
      } else if (e !== undefined) value = e as InputValue;

      const mapped = customMap ? customMap(value, values[key]) : value;

      setValues((prev) => ({
        ...prev,
        [key]: mask ? applyMaskTyped(mask, String(mapped)) : mapped
      }));

      setErrors((prev) => ({ ...prev, [key]: undefined }));
    };

  const triggerPartialValidation = async (
    fields: (keyof T)[]
  ): Promise<Partial<Record<keyof T, string>>> => {
    if (!validationSchema) return {};

    const fieldErrors: Partial<Record<keyof T, string>> = {};

    await Promise.all(
      fields.map(async (field) => {
        try {
          await validationSchema.validateAt(String(field), values);
          setErrors((prev) => ({ ...prev, [field]: undefined }));
        } catch (err) {
          const validationErr = err as ValidationError;
          const fieldValue = values[field];

          if (Array.isArray(fieldValue)) {
            const errorArray = new Array(fieldValue.length).fill(undefined);
            // eslint-disable-next-line prefer-named-capture-group -- need capture group for index
            const regex = /\[(\d+)\]/;
            const match = regex.exec(validationErr.path ?? "");
            if (match) {
              const index = parseInt(match[1], 10);
              errorArray[index] = validationErr.message;
            }
            fieldErrors[field] = errorArray as any;
            setErrors((prev) => ({ ...prev, [field]: errorArray as any }));
          } else {
            fieldErrors[field] = validationErr.message;
            setErrors((prev) => ({ ...prev, [field]: validationErr.message }));
          }
        }
      })
    );

    return fieldErrors;
  };

  const triggerValidation = async (): Promise<
    Partial<Record<keyof T, string>>
  > => {
    if (!validationSchema) return {};

    try {
      await validationSchema.validate(values, { abortEarly: false });
      setErrors({});
      return {};
    } catch (err) {
      const validationErr = err as ValidationError;
      const newErrors = validationErr.inner.length
        ? validationErr.inner.reduce<Partial<Record<keyof T, string>>>(
            (acc, curr) => {
              const field = curr.path?.split("[")[0] as keyof T;
              const fieldValue = values[field];

              if (Array.isArray(fieldValue)) {
                if (!acc[field])
                  acc[field] = new Array(fieldValue.length).fill(
                    undefined
                  ) as any;

                // eslint-disable-next-line prefer-named-capture-group -- need capture group for index
                const regex = /\[(\d+)\]/;
                const match = regex.exec(curr.path ?? "");
                if (match) {
                  const index = parseInt(match[1], 10);
                  (acc[field] as any)[index] = curr.message;
                }
              } else acc[curr.path as keyof T] = curr.message;
              return acc;
            },
            {}
          )
        : {};

      setErrors(newErrors);
      return newErrors;
    }
  };

  const onSubmit: SubmitTyped<T> = ({ onSuccess, onError }) => {
    return async (e) => {
      e.preventDefault();
      setIsSubmitting(true);

      if (!validationSchema)
        return (
          onSuccess &&
          Promise.resolve(onSuccess(values)).finally(() => {
            setIsSubmitting(false);
          })
        );

      const validationErrors = await triggerValidation();

      if (Object.keys(validationErrors).length === 0) await onSuccess?.(values);
      else await onError?.(validationErrors);

      setIsSubmitting(false);
    };
  };

  const onPartialSubmit: SubmitTyped<T> = ({ onSuccess, onError }) => {
    return async (e) => {
      e.preventDefault();
      setIsSubmitting(true);

      if (!validationSchema)
        return (
          onSuccess &&
          Promise.resolve(onSuccess(values)).finally(() => {
            setIsSubmitting(false);
          })
        );

      const fields = Object.keys(values) as (keyof T)[];

      const validationErrors = await triggerPartialValidation(fields);

      if (Object.keys(validationErrors).length === 0) await onSuccess?.(values);
      else await onError?.(validationErrors);

      setIsSubmitting(false);
    };
  };

  return {
    values,
    errors,
    isSubmitting,
    handleChange,
    triggerValidation,
    triggerPartialValidation,
    onSubmit,
    onPartialSubmit,
    setErrors,
    reset: (resetValues?: T) => {
      setValues(resetValues || initialValues);
      setErrors({});
    },
    register: ((key, map, mask) => ({
      name: String(key),
      value: values[key],
      onChange: handleChange(key, map, mask),
      error: errors[key]
    })) as RegisterTyped<T>
  };
};
